/**
 * ManagerHome — Home FinOps do gestor (escopo podado pelo backend ao caller).
 *
 * Monta os widgets SP3 a partir dos hooks de dados já prontos. Todo o escopo é
 * aplicado server-side (SP1/SP0 §5.1): os endpoints de plataforma já filtram
 * para não-admin, então o gestor recebe apenas as contas do scope dele.
 *
 * Acesso (§3.1):
 *   - scope_reason 'no_access'            → "Você não tem acesso a custos."
 *   - scope_reason 'tenant_without_account' → "Sua organização ainda não tem
 *     conta AWS vinculada."
 *   - cost_available=false                → banner de fonte indisponível
 *     (modo legado / não confiável — NÃO erro 500).
 *
 * Drill-down (RF4): conta selecionada e serviço selecionado vivem na URL
 * (?account_id=&service=). Clicar numa conta refiltra o breakdown por serviço
 * àquela conta; clicar num serviço aplica cross-filter (refletido na URL).
 */
import { Stack, SimpleGrid, Alert, Skeleton, Group, Button, Title, Text } from '@mantine/core'
import { IconInfoCircle, IconAlertTriangle, IconX } from '@tabler/icons-react'
import { useSearchParams } from 'react-router-dom'
import { useFinopsPeriod } from '@/hooks/useFinopsPeriod'
import { useFinopsPortfolio } from '@/hooks/useFinopsPortfolio'
import { useFinopsCostBreakdown } from '@/hooks/useFinopsCostBreakdown'
import { useFinopsTrend } from '@/hooks/useFinopsTrend'
import { useFinopsScope } from '@/hooks/useFinopsScope'
import { PeriodPicker } from '@/components/finops/PeriodPicker'
import { KpiRow } from '@/components/finops/widgets/KpiRow'
import { CostByAccountCard } from '@/components/finops/widgets/CostByAccountCard'
import { CostByServiceCard } from '@/components/finops/widgets/CostByServiceCard'
import { TrendCard } from '@/components/finops/widgets/TrendCard'
import { TopMoversCard } from '@/components/finops/widgets/TopMoversCard'
import { AnomalyBanner } from '@/components/finops/widgets/AnomalyBanner'
import { BudgetSemaphoreCard } from '@/components/finops/widgets/BudgetSemaphoreCard'
import type { PortfolioResponse } from '@/api/finopsDashboard'

export interface FinopsHomeBodyProps {
  /** Slot extra renderizado no fim (PlatformHome injeta PortfolioTable + contas não mapeadas). */
  footerSlot?: (portfolio: PortfolioResponse) => React.ReactNode
  /** Título da página. */
  title?: string
  /** Subtítulo da página. */
  subtitle?: string
}

/**
 * Corpo compartilhado da Home FinOps (gestor e admin usam os mesmos widgets).
 * PlatformHome reusa via `footerSlot` para anexar a tabela de portfólio.
 */
export function FinopsHomeBody({ footerSlot, title, subtitle }: FinopsHomeBodyProps) {
  const period = useFinopsPeriod()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedAccount = searchParams.get('account_id') ?? undefined
  const selectedService = searchParams.get('service') ?? undefined

  const portfolioQ = useFinopsPortfolio({ start: period.start, end: period.end })
  const portfolio = portfolioQ.data

  // Breakdown por conta (escopo de plataforma — backend poda para não-admin).
  const byAccountQ = useFinopsCostBreakdown({
    start: period.start,
    end: period.end,
    scope: 'platform',
    groupBy: 'account',
  })

  // Breakdown por serviço; se uma conta está selecionada, refiltra àquela conta.
  const byServiceQ = useFinopsCostBreakdown({
    start: period.start,
    end: period.end,
    scope: 'platform',
    groupBy: 'service',
    accountId: selectedAccount,
  })

  const trendQ = useFinopsTrend({
    start: period.start,
    end: period.end,
    granularity: 'MONTHLY',
    scope: 'platform',
  })

  const { accessMessage } = useFinopsScope(portfolio?.scope_reason)

  // ── Drill-down handlers (refletem na URL) ──
  function selectAccount(accountId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (next.get('account_id') === accountId) {
        next.delete('account_id')
      } else {
        next.set('account_id', accountId)
      }
      next.delete('service')
      return next
    })
  }

  function selectService(service: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (next.get('service') === service) {
        next.delete('service')
      } else {
        next.set('service', service)
      }
      return next
    })
  }

  function clearFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('account_id')
      next.delete('service')
      return next
    })
  }

  const header = (
    <Group justify="space-between" align="flex-end" wrap="wrap">
      <Stack gap={2}>
        <Title order={2}>{title ?? 'FinOps'}</Title>
        {subtitle && (
          <Text size="sm" c="var(--ai-text-secondary)">
            {subtitle}
          </Text>
        )}
      </Stack>
      <PeriodPicker />
    </Group>
  )

  // ── Estados de carregamento / acesso ──
  if (portfolioQ.isLoading) {
    return (
      <Stack gap="xl">
        {header}
        <Skeleton height={120} radius="md" />
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Skeleton height={300} radius="md" />
          <Skeleton height={300} radius="md" />
        </SimpleGrid>
      </Stack>
    )
  }

  // Acesso negado / sem conta vinculada (§3.1) — mensagens distintas.
  if (accessMessage) {
    return (
      <Stack gap="xl">
        {header}
        <Alert color="yellow" icon={<IconInfoCircle size={18} />} title="Sem custos para exibir">
          {accessMessage}
        </Alert>
      </Stack>
    )
  }

  // Fonte de custo indisponível (FINOPS_COST_FACTS_ENABLED OFF) — não é erro 500.
  const sourceUnavailable = portfolio ? portfolio.cost_available === false : false

  return (
    <Stack gap="xl">
      {header}

      {sourceUnavailable && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={18} />}
          title="Fonte de custo indisponível"
        >
          Os números estão em modo legado (custo não-misturado) e podem não ser
          confiáveis. A base de custos consolidada (cost_facts) ainda não está
          disponível neste ambiente.
        </Alert>
      )}

      <AnomalyBanner anomalyTenants={(portfolio?.tenants ?? []).filter((t) => t.anomaly)} />

      {portfolio && (
        <KpiRow kpis={portfolio.kpis} is_partial={portfolio.is_partial} as_of={portfolio.as_of} />
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {portfolio && (
          <BudgetSemaphoreCard
            // Base coerente: compara o custo SÓ dos tenants orçados vs o orçamento
            // total cadastrado (budgeted_cost_usd/budget_total_usd) — não total_usd,
            // que incluía tenants sem orçamento e inflava o % (ex.: 4327%).
            pct_used={
              portfolio.kpis.budget_total_usd > 0
                ? (portfolio.kpis.budgeted_cost_usd / portfolio.kpis.budget_total_usd) * 100
                : null
            }
            budget_usd={portfolio.kpis.budget_total_usd}
            amount_usd={portfolio.kpis.budgeted_cost_usd}
          />
        )}
        <TopMoversCard rows={portfolio?.tenants ?? []} />
      </SimpleGrid>

      {(selectedAccount || selectedService) && (
        <Group>
          <Button
            size="xs"
            variant="light"
            color="gray"
            leftSection={<IconX size={14} />}
            onClick={clearFilters}
          >
            Limpar filtros
            {selectedAccount ? ` · conta ${selectedAccount}` : ''}
            {selectedService ? ` · serviço ${selectedService}` : ''}
          </Button>
        </Group>
      )}

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {byAccountQ.data && (
          <CostByAccountCard data={byAccountQ.data} onSelect={selectAccount} />
        )}
        {byServiceQ.data && (
          <CostByServiceCard
            data={byServiceQ.data}
            onSelect={selectService}
            title={
              selectedAccount
                ? `Custo por serviço AWS · conta ${selectedAccount}`
                : undefined
            }
          />
        )}
      </SimpleGrid>

      {trendQ.data && <TrendCard data={trendQ.data} />}

      {footerSlot && portfolio && footerSlot(portfolio)}
    </Stack>
  )
}

export function ManagerHome() {
  return (
    <FinopsHomeBody
      title="FinOps"
      subtitle="Custos das contas da sua organização"
    />
  )
}
